import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { withStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ScheduleIcon from "@material-ui/icons/Schedule";

import { EU_STOCK_FLAG_IMAGE } from "./stockFlagImages";

// EU blue matches the flag; amber signals the 2-3 month wait.
export const EU_STOCK_TONE = {
  blue: "#1d4ed8",
  accent: "#d97706",
  noticeBg: "#fff7ed",
  noticeBorder: "#fed7aa",
  title: "#9a3412",
  text: "#7c2d12",
};

export function europeanStockCardStyles() {
  return {
    euCard: {
      borderTop: `4px solid ${EU_STOCK_TONE.accent}`,
    },
  };
}

const styles = (theme) => ({
  badge: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    borderRadius: 999,
    color: "#fff",
    backgroundColor: EU_STOCK_TONE.blue,
    fontWeight: 600,
    padding: "2px 10px 2px 4px",
    whiteSpace: "nowrap",
  },
  flag: {
    display: "block",
    width: 18,
    height: 12,
    borderRadius: 2,
    marginRight: 6,
    objectFit: "cover",
  },
  notice: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    backgroundColor: EU_STOCK_TONE.noticeBg,
    border: `1px solid ${EU_STOCK_TONE.noticeBorder}`,
    borderLeft: `4px solid ${EU_STOCK_TONE.accent}`,
  },
  noticeTitle: {
    display: "flex",
    alignItems: "center",
    fontWeight: 700,
    color: EU_STOCK_TONE.title,
  },
  noticeIcon: {
    fontSize: 20,
    marginRight: theme.spacing(0.75),
  },
  noticeLine: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: EU_STOCK_TONE.text,
  },
  infoTrigger: {
    display: "inline-flex",
    verticalAlign: "middle",
    marginLeft: theme.spacing(0.5),
    color: EU_STOCK_TONE.title,
    cursor: "help",
    borderRadius: "50%",
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  infoIcon: {
    fontSize: 16,
  },
  eta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing(0.75),
    color: EU_STOCK_TONE.title,
    fontWeight: 700,
  },
  etaIcon: {
    fontSize: 18,
    marginRight: theme.spacing(0.5),
  },
});

function InfoTip({ classes, text }) {
  if (!text) {
    return null;
  }

  return (
    <Tooltip title={text} arrow enterTouchDelay={0} leaveTouchDelay={6000}>
      <span className={classes.infoTrigger} tabIndex={0} role="img" aria-label={text}>
        <InfoOutlinedIcon className={classes.infoIcon} aria-hidden="true" />
      </span>
    </Tooltip>
  );
}

InfoTip.propTypes = {
  classes: PropTypes.object.isRequired,
  text: PropTypes.string,
};

function EuropeanStockBadgeBase({ classes, label, className }) {
  return (
    <span className={classNames(classes.badge, className)}>
      <img src={EU_STOCK_FLAG_IMAGE} alt="" aria-hidden="true" className={classes.flag} />
      <Typography variant="caption" component="span">
        {label}
      </Typography>
    </span>
  );
}

EuropeanStockBadgeBase.propTypes = {
  classes: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};

function EuropeanStockNoticeBase({ classes, details, className }) {
  const lines = (details && details.lines) || [];

  return (
    <div className={classNames(classes.notice, className)} role="note">
      <Typography variant="body2" component="div" className={classes.noticeTitle}>
        <ScheduleIcon className={classes.noticeIcon} aria-hidden="true" />
        {details.arrivalLabel}
      </Typography>
      {lines.map((line, index) => (
        <Typography
          key={`${index}-${line}`}
          variant="caption"
          className={classes.noticeLine}
        >
          {line}
          {index === 0 ? <InfoTip classes={classes} text={details.tooltip} /> : null}
        </Typography>
      ))}
    </div>
  );
}

EuropeanStockNoticeBase.propTypes = {
  classes: PropTypes.object.isRequired,
  details: PropTypes.shape({
    arrivalLabel: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.string),
    tooltip: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
};

function EuropeanStockEtaBase({ classes, label, className }) {
  if (!label) {
    return null;
  }

  return (
    <Typography variant="body2" component="div" className={classNames(classes.eta, className)}>
      <ScheduleIcon className={classes.etaIcon} aria-hidden="true" />
      {label}
    </Typography>
  );
}

EuropeanStockEtaBase.propTypes = {
  classes: PropTypes.object.isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
};

export const EuropeanStockBadge = withStyles(styles)(EuropeanStockBadgeBase);
export const EuropeanStockNotice = withStyles(styles)(EuropeanStockNoticeBase);
export const EuropeanStockEta = withStyles(styles)(EuropeanStockEtaBase);
